/**
 * FERP Portal - Task List Page (DRR-074: Tabulator DRY Template, DRR-141: Subject Scope)
 *
 * EXECUTE Phase: Life-critical work items for patient care, financial, and compliance actions.
 * Delegates to TabulatorPrimitiveManager for consistent CRUD across all 25 primitives.
 *
 * Subject-filter pattern (All / My Tasks / Resident) is provided by the reusable
 * SubjectFilterHeader + useSubjectScope template from @ferp/workflow-forms (DRR-141).
 * Updating that template automatically updates this page and all other primitive pages.
 *
 * DRR-117/118: subjectActorId enables CNA workflow — filter tasks by resident/patient.
 * DRR-085: Subject constellation drill-down shows 5W1H care team context.
 * DRR-141: listSubjectsWithLifecycle replaces listActors(200)+client-side filter.
 *
 * DRR-189 WS-2: USER-layer defaults to "My Tasks" filter and card view.
 * RA/CNA sees their assigned tasks as cards — 3-tap completion flow.
 * View mode default is passed via TabulatorPrimitiveManager.defaultViewMode.
 */

import { useMemo, useEffect, useState, useCallback } from 'react';
import { SegmentedControl, Group, Text, Stack, Paper, Alert, Badge } from '@mantine/core';
import { IconInfoCircle, IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import { useDevActorId } from '@/hooks/useDevActorId';
import { useResolveActorName } from '@/contexts/ActorNameContext';
import {
  useGetTasksByActorQuery,
  useGetTasksBySubjectQuery,
  useGetTaskEnrichmentContextQuery,
  useClaimTaskMutation,
  useAssignTaskToActorMutation,
} from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import {
  useSubjectScope,
  SubjectFilterHeader,
} from '@ferp/workflow-forms';
import { useViewLayer } from '@/auth/AuthContext';

/** DRR-178/DRR-256: Scope dimensions for task grouping */
const TASK_GROUP_DIMENSIONS = [
  { value: 'subjectActorId', label: 'Subject' }, // Default: resident/client/project (DRR-256)
  { value: 'taskGroupRef', label: 'Work Type' }, // Task category/type
  { value: 'status', label: 'Status' },
  { value: 'safetyLevel', label: 'Safety' },
  { value: 'taskType', label: 'Task Type' },
] as const;

export function TaskListPage() {
  const viewLayer = useViewLayer();
  const { actorId, actorName, loading: actorLoading } = useDevActorId();
  const resolveActorName = useResolveActorName();

  // DRR-141: Subject scope — all filter state, subject dropdown, lifecycle phases,
  // and constellation context in one hook. Update useSubjectScope to update all pages.
  const scope = useSubjectScope({ nameResolver: resolveActorName });
  const { filter, selectedSubjectId, derivedGroupBy, derivedFilterBy } = scope;

  // DRR-189 WS-2: USER layer defaults to card view + "My Tasks".
  const isUserLayer = viewLayer === 'user';

  // DRR-178: User-selected grouping dimension. Persisted to localStorage.
  // Default to Subject (resident/client/project) - the universal "who is this work for" dimension.
  // Work Type (taskGroupRef) is secondary - useful for admins viewing task categories.
  const [selectedDimension, setSelectedDimension] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('ferp.tasks.groupBy') ?? 'subjectActorId';
    }
    return 'subjectActorId';
  });

  // Persist dimension selection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('ferp.tasks.groupBy', selectedDimension);
    }
  }, [selectedDimension]);

  // DRR-178: effectiveGroupBy resolves from scope or user selection.
  // Scope-driven grouping (lifecyclePhase when subject selected) takes precedence.
  const effectiveGroupBy = useMemo(() => {
    // Scope-driven grouping takes precedence (e.g., lifecyclePhase when subject selected)
    if (derivedGroupBy) return derivedGroupBy;
    // User-selected dimension applies to My Tasks and Subject tabs
    if (filter === 'my' || filter === 'subject') return selectedDimension;
    // All Tasks: use user selection or default
    return selectedDimension;
  }, [derivedGroupBy, filter, selectedDimension]);

  // DRR-189 WS-2: Auto-switch to "My Tasks" for USER layer on first visit.
  useEffect(() => {
    if (isUserLayer && scope.filter === 'all' && actorId) {
      scope.setFilter('my');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorId, isUserLayer]);

  // "My Tasks" — backend-filtered via Valkey O(1) index
  const {
    data: myTasksData,
    loading: myTasksLoading,
    error: myTasksError,
    refetch: myTasksRefetch,
  } = useGetTasksByActorQuery({
    variables: { actorId: actorId ?? '' },
    skip: filter !== 'my' || !actorId,
  });

  // "Resident" tab — backend-filtered via Valkey subject index
  const {
    data: subjectTasksData,
    loading: subjectTasksLoading,
    error: subjectTasksError,
    refetch: subjectTasksRefetch,
  } = useGetTasksBySubjectQuery({
    variables: { subjectActorId: selectedSubjectId ?? '' },
    skip: filter !== 'subject' || !selectedSubjectId,
  });

  // DRR-149: Task enrichment context for subject (allergy alerts, drug interactions)
  // LIFE-CRITICAL: Med Aide sees enrichment before task completion
  const { data: enrichmentData } = useGetTaskEnrichmentContextQuery({
    variables: { subjectActorId: selectedSubjectId ?? '' },
    skip: filter !== 'subject' || !selectedSubjectId,
  });

  // Task claim mutation for inbox tasks
  const [claimTask] = useClaimTaskMutation();

  // DRR-125: Task assignment mutation for supervisor assignment flow
  const [assignTaskToActor] = useAssignTaskToActorMutation();

  const handleClaimTask = useCallback(async (taskId: string) => {
    try {
      await claimTask({ variables: { id: taskId } });
      notifications.show({
        title: 'Task claimed',
        message: 'Task has been assigned to you',
        color: 'green',
      });
      myTasksRefetch();
    } catch (err) {
      notifications.show({
        title: 'Failed to claim task',
        message: String(err),
        color: 'red',
      });
    }
  }, [claimTask, myTasksRefetch]);

  // DRR-125: Assign task to specific actor (supervisor workflow)
  const handleAssignTask = useCallback(async (taskId: string, targetActorId: string) => {
    try {
      await assignTaskToActor({
        variables: {
          actorId: targetActorId,
          input: { taskId },
        },
      });
      notifications.show({
        title: 'Task assigned',
        message: 'Task has been assigned successfully',
        color: 'green',
      });
      myTasksRefetch();
    } catch (err) {
      notifications.show({
        title: 'Failed to assign task',
        message: String(err),
        color: 'red',
      });
    }
  }, [assignTaskToActor, myTasksRefetch]);

  const activeOverride = useMemo(() => {
    if (filter === 'my') {
      return {
        items: (myTasksData?.getTasksByActor ?? null) as PrimitiveItem[] | null,
        loading: myTasksLoading || actorLoading,
        error: myTasksError,
        refetch: myTasksRefetch,
      };
    }
    if (filter === 'subject' && selectedSubjectId) {
      return {
        items: (subjectTasksData?.getTasksBySubject ?? null) as PrimitiveItem[] | null,
        loading: subjectTasksLoading,
        error: subjectTasksError,
        refetch: subjectTasksRefetch,
      };
    }
    return undefined;
  }, [
    filter, selectedSubjectId,
    myTasksData, myTasksLoading, myTasksError, myTasksRefetch, actorLoading,
    subjectTasksData, subjectTasksLoading, subjectTasksError, subjectTasksRefetch,
  ]);

  // Parse enrichment context for display
  const enrichmentContext = enrichmentData?.taskEnrichmentContext as Record<string, unknown> | null;
  const hasAllergyAlert = enrichmentContext?.allergy_match_found === true;
  const hasInteractionWarning = (enrichmentContext?.interaction_severity as string) === 'HIGH';
  const hasControlledSubstance = enrichmentContext?.has_schedule_ii_medication === true;

  const headerSlot = (
    <Stack gap="xs">
      <SubjectFilterHeader
        scope={scope}
        actorName={actorName}
        actorLoading={actorLoading}
        primitiveLabel="Tasks"
      />
      {/* DRR-149: Enrichment alerts for subject (medication safety) */}
      {filter === 'subject' && selectedSubjectId && enrichmentContext && (
        <Paper p="xs" withBorder radius="sm" bg="gray.0">
          <Group gap="md">
            {hasAllergyAlert && (
              <Badge color="red" leftSection={<IconAlertCircle size={12} />}>
                Allergy Alert: {typeof enrichmentContext.allergen_name === 'string' ? enrichmentContext.allergen_name : 'Unknown'}
              </Badge>
            )}
            {hasInteractionWarning && (
              <Badge color="orange" leftSection={<IconAlertCircle size={12} />}>
                Drug Interaction Warning
              </Badge>
            )}
            {hasControlledSubstance && (
              <Badge color="violet" leftSection={<IconInfoCircle size={12} />}>
                Controlled Substance
              </Badge>
            )}
            {!hasAllergyAlert && !hasInteractionWarning && !hasControlledSubstance && (
              <Text size="xs" c="dimmed">No medication alerts</Text>
            )}
          </Group>
        </Paper>
      )}
      {/* DRR-178: Dimension picker for task grouping */}
      <Group gap="xs">
        <Text size="sm" c="dimmed">Group by:</Text>
        <SegmentedControl
          size="xs"
          data={TASK_GROUP_DIMENSIONS.map(d => ({ value: d.value, label: d.label }))}
          value={selectedDimension}
          onChange={setSelectedDimension}
        />
      </Group>
    </Stack>
  );

  // DRR-125: Expose task action handlers for card actions (used by TabulatorPrimitiveManager)
  // These handlers are wired to the component but UI buttons will be added in future sprint
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _taskActions = { claim: handleClaimTask, assign: handleAssignTask };

  return (
    <TabulatorPrimitiveManager
      primitiveType="task"
      headerSlot={headerSlot}
      overrideData={activeOverride}
      groupBy={effectiveGroupBy}
      filterBy={derivedFilterBy}
      defaultViewMode={isUserLayer ? 'card' : undefined}
    />
  );
}

export default TaskListPage;
